'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit2, Trash2, Eye, EyeOff, Star, ChevronLeft, ChevronRight, Loader2, Save,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface Testimonial {
  id: string;
  quote: string;
  author_name: string;
  author_role: string;
  avatar_url: string;
  rating: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

const emptyForm: Omit<Testimonial, 'id' | 'created_at'> = {
  quote: '',
  author_name: '',
  author_role: '',
  avatar_url: '',
  rating: 5,
  is_active: true,
  sort_order: 0,
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={12}
          className={s <= rating ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}
        />
      ))}
    </div>
  );
}

export default function TestimonialsContent() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<Testimonial | null>(null);
  const [form, setForm] = useState<Omit<Testimonial, 'id' | 'created_at'>>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: '',
    name: '',
  });
  const [deleting, setDeleting] = useState(false);
  const perPage = 8;

  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('testimonials')
      .select('*')
      .order('sort_order', { ascending: true });
    setTestimonials(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const filtered = testimonials.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.author_name.toLowerCase().includes(q) ||
      t.author_role.toLowerCase().includes(q) ||
      t.quote.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (t: Testimonial) => {
    setEditItem(t);
    setForm({
      quote: t.quote,
      author_name: t.author_name,
      author_role: t.author_role,
      avatar_url: t.avatar_url,
      rating: t.rating,
      is_active: t.is_active,
      sort_order: t.sort_order,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.author_name.trim() || !form.quote.trim()) {
      toast.error('Author name and quote are required');
      return;
    }
    setSaving(true);
    const supabase = createClient();
    if (editItem) {
      const { error } = await supabase
        .from('testimonials')
        .update(form)
        .eq('id', editItem.id);
      if (error) {
        toast.error('Failed to update testimonial');
      } else {
        toast.success('Testimonial updated');
        setModalOpen(false);
        await fetchTestimonials();
      }
    } else {
      const { error } = await supabase.from('testimonials').insert(form);
      if (error) {
        toast.error('Failed to create testimonial');
      } else {
        toast.success('Testimonial created');
        setModalOpen(false);
        await fetchTestimonials();
      }
    }
    setSaving(false);
  };

  const handleToggleActive = async (t: Testimonial) => {
    const supabase = createClient();
    const { error } = await supabase
      .from('testimonials')
      .update({ is_active: !t.is_active })
      .eq('id', t.id);
    if (error) {
      toast.error('Failed to update status');
    } else {
      toast.success(t.is_active ? 'Testimonial hidden' : 'Testimonial published');
      await fetchTestimonials();
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const supabase = createClient();
    const { error } = await supabase.from('testimonials').delete().eq('id', deleteModal.id);
    if (error) {
      toast.error('Failed to delete testimonial');
    } else {
      toast.success('Testimonial deleted');
      setDeleteModal({ open: false, id: '', name: '' });
      await fetchTestimonials();
    }
    setDeleting(false);
  };

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-700 text-foreground">Testimonials</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage customer testimonials shown on the platform
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-600 hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          Add Testimonial
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total', value: testimonials.length },
          { label: 'Active', value: testimonials.filter((t) => t.is_active).length },
          {
            label: 'Avg Rating',
            value:
              testimonials.length > 0
                ? (testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length).toFixed(1)
                : '—',
          },
        ].map((s) => (
          <div key={s.label} className="card-elevated rounded-xl px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className="text-2xl font-700 font-tabular text-foreground">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="card-elevated rounded-xl p-4 mb-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search testimonials..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Author</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Quote</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Rating</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Order</th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    No testimonials found
                  </td>
                </tr>
              ) : (
                paginated.map((t) => (
                  <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-600 text-foreground">{t.author_name}</p>
                      <p className="text-xs text-muted-foreground">{t.author_role}</p>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-muted-foreground line-clamp-2 text-xs">{t.quote}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StarRating rating={t.rating} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={t.is_active ? 'active' : 'inactive'} />
                    </td>
                    <td className="px-4 py-3 font-tabular text-muted-foreground">{t.sort_order}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleActive(t)}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title={t.is_active ? 'Hide' : 'Publish'}
                        >
                          {t.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => openEdit(t)}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, id: t.id, name: t.author_name })}
                          className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-muted-foreground hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              {filtered.length} testimonials · Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? 'Edit Testimonial' : 'Add Testimonial'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Author Name *</label>
            <input
              type="text"
              value={form.author_name}
              onChange={(e) => setForm((f) => ({ ...f, author_name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="e.g. Ahmed Al-Rashidi"
            />
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Author Role</label>
            <input
              type="text"
              value={form.author_role}
              onChange={(e) => setForm((f) => ({ ...f, author_role: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="e.g. Customer · Muscat"
            />
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Quote *</label>
            <textarea
              value={form.quote}
              onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              placeholder="Testimonial text..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Rating (1–5)</label>
              <input
                type="number"
                min={1}
                max={5}
                value={form.rating}
                onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Sort Order</label>
              <input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Avatar URL</label>
            <input
              type="text"
              value={form.avatar_url}
              onChange={(e) => setForm((f) => ({ ...f, avatar_url: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="https://..."
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="t-active"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="t-active" className="text-sm text-foreground">
              Active (visible on site)
            </label>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-500 text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-600 hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editItem ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: '', name: '' })}
        title="Delete Testimonial"
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to delete the testimonial from{' '}
          <span className="font-600 text-foreground">{deleteModal.name}</span>? This cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => setDeleteModal({ open: false, id: '', name: '' })}
            className="px-4 py-2 text-sm font-500 text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-600 hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            {deleting && <Loader2 size={14} className="animate-spin" />}
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
